var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useContext, useEffect, useState, useMemo } from "react";
import { Box, Grid, Paper, Table, TableBody, TableContainer, TableHead, TableRow, Typography, CircularProgress, useTheme, TablePagination, Switch, } from "@mui/material";
import { StyledTableCell, StyledTableRow, TableRowsLoader, } from "../../components/mui-table/mui-table-helper";
import { Pencil, Trash, CaretRight, CaretDown, Warning, } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import ObjectStatusFormSubmit from "./ObjectStatusFormSubmit";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, } from "@mui/material";
import ObjectStatusApi from "../../../infrastructure/api/pages/object-status-api";
import { filterFieldSetter, useSessionFilterMemory, } from "../../hooks/useSessionFilterMemory";
import { Layout } from "../../components/Layout/Layout";
import { useTranslation } from "react-i18next";
import ObjectStatusTopHeader from "./ObjectStatusTopHeader";
import { UserRolesPermissionsContext } from "../../../context/UserRolesPermissionsContext";
import { checkCreateEdit, checkDelete, hasFullAccess, } from "../../../application/common/utils/roles-helper";
import { Modules } from "../../../application/common/enums/screen-name";
const initialFormData = {
    id: "",
    name: "",
    parentId: null,
    level: 1,
    sortOrder: 0, // Auto-calculated by backend
    isActive: true,
    isSystemStatus: false,
    children: [],
};
// Helper function: Check if node or any child matches search query
const nodeMatchesSearch = (node, query) => {
    if (!query.trim())
        return true;
    const lowerQuery = query.toLowerCase().trim();
    // Check if current node matches
    if (node.name.toLowerCase().includes(lowerQuery)) {
        return true;
    }
    // Check if any child matches
    if (node.children && node.children.length > 0) {
        return node.children.some((child) => nodeMatchesSearch(child, query));
    }
    return false;
};
// Helper function: Filter tree based on search query
const filterTree = (nodes, query) => {
    if (!query.trim())
        return { filtered: nodes, nodesToExpand: [] };
    const nodesToExpand = [];
    const filtered = nodes.reduce((result, node) => {
        if (nodeMatchesSearch(node, query)) {
            const filteredNode = Object.assign({}, node);
            // Recursively filter children
            if (filteredNode.children && filteredNode.children.length > 0) {
                const childResult = filterTree(filteredNode.children, query);
                filteredNode.children = childResult.filtered;
                nodesToExpand.push(...childResult.nodesToExpand);
                // Auto-expand nodes that have matching children
                if (filteredNode.children.length > 0) {
                    nodesToExpand.push(node.id);
                }
            }
            result.push(filteredNode);
        }
        return result;
    }, []);
    return { filtered, nodesToExpand };
};
// Helper function: Flatten tree structure for table display
const flattenTree = (nodes, expandedRows, parentId = null, isVisible = true) => {
    let result = [];
    nodes.forEach((node) => {
        const hasChildren = !!(node.children && node.children.length > 0);
        result.push(Object.assign(Object.assign({}, node), { isVisible,
            hasChildren }));
        if (hasChildren) {
            const isExpanded = expandedRows.has(node.id);
            const childrenVisible = isVisible && isExpanded;
            result = result.concat(flattenTree(node.children, expandedRows, node.id, childrenVisible));
        }
    });
    return result;
};
const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];
const DEFAULT_FILTERS = {
    searchQuery: "",
    page: 0,
    rowsPerPage: 10,
};
const revalidateFilters = (restored) => (Object.assign(Object.assign({}, restored), { searchQuery: typeof restored.searchQuery === "string" ? restored.searchQuery : "", page: Number.isInteger(restored.page) && restored.page >= 0 ? restored.page : 0, 
    // Must be one of the offered sizes, otherwise TablePagination throws on an
    // out-of-range value.
    rowsPerPage: ROWS_PER_PAGE_OPTIONS.includes(restored.rowsPerPage)
        ? restored.rowsPerPage
        : DEFAULT_FILTERS.rowsPerPage }));
export const ObjectStatus = () => {
    const theme = useTheme();
    const [statuses, setStatuses] = useState([]);
    const [status, setStatus] = useState(initialFormData);
    const [refreshGrid, setRefreshGrid] = useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [idToBeDeleted, setIdToBeDeleted] = useState();
    const { t: translate } = useTranslation();
    const { RolesPermissions } = useContext(UserRolesPermissionsContext);
    const [modalTitle, setModalTitle] = useState("");
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [statusToDelete, setStatusToDelete] = useState(null);
    // Filter memory (item #2): search term + paging survive leaving the screen.
    const [filters, setFilters] = useSessionFilterMemory("object-status", DEFAULT_FILTERS, revalidateFilters);
    const { searchQuery, page, rowsPerPage } = filters;
    const setPage = filterFieldSetter(setFilters, "page");
    // Check user permission levels for object status management
    // Note: For ObjectStatus management, partial access behaves same as read-only (view only)
    const isFullAccess = hasFullAccess(RolesPermissions, Modules.ChangeObjectStatus);
    const canCreateEdit = checkCreateEdit(RolesPermissions, Modules.ChangeObjectStatus);
    const canDelete = checkDelete(RolesPermissions, Modules.ChangeObjectStatus);
    // Handle status toggle
    const handleStatusToggle = (statusId, isActive) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        try {
            const result = yield ObjectStatusApi.updateStatus(statusId, isActive);
            if (result.status === 200) {
                toast.success(translate("StatusUpdatedSuccessfully"));
                // Update the local state to reflect the change immediately
                setStatuses(prevStatuses => updateStatusInTree(prevStatuses, statusId, isActive));
            }
        }
        catch (error) {
            const errorMessage = ((_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error) ||
                ((_c = error === null || error === void 0 ? void 0 : error.response) === null || _c === void 0 ? void 0 : _c.data) ||
                (error === null || error === void 0 ? void 0 : error.message);
            if (errorMessage === null || errorMessage === void 0 ? void 0 : errorMessage.includes("system status")) {
                toast.error(translate("CannotUpdateSystemStatus"));
            }
            else {
                toast.error(translate("ErrorUpdateStatus"));
            }
            // Refresh to revert the UI if update failed
            setRefreshGrid(!refreshGrid);
        }
    });
    // Helper function to update status in tree structure
    const updateStatusInTree = (nodes, statusId, isActive) => {
        return nodes.map(node => {
            if (node.id === statusId) {
                return Object.assign(Object.assign({}, node), { isActive });
            }
            if (node.children && node.children.length > 0) {
                return Object.assign(Object.assign({}, node), { children: updateStatusInTree(node.children, statusId, isActive) });
            }
            return node;
        });
    };
    // Toggle expand/collapse for a row
    const toggleRowExpansion = (id) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        }
        else {
            newExpanded.add(id);
        }
        setExpandedRows(newExpanded);
    };
    // Search handler
    const handleSearchChange = (query) => {
        // One update, so the search term and the page reset land together.
        setFilters((prev) => (Object.assign(Object.assign({}, prev), { searchQuery: query, page: 0 })));
    };
    // Note: Removed automatic page reset on statuses change to maintain current page after deletion
    useEffect(() => {
        const fetchStatuses = () => __awaiter(void 0, void 0, void 0, function* () {
            setLoading(true);
            try {
                const response = yield ObjectStatusApi.getTree();
                if (response.status === 200 && Array.isArray(response.data)) {
                    setStatuses(response.data);
                }
            }
            finally {
                setLoading(false);
            }
        });
        fetchStatuses();
    }, [refreshGrid]);
    const handleDeleteClick = (status) => {
        setStatusToDelete(status);
        setDeleteConfirmOpen(true);
    };
    const handleDeleteConfirm = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        if (!(statusToDelete === null || statusToDelete === void 0 ? void 0 : statusToDelete.id))
            return;
        setDeleting(true);
        setIdToBeDeleted(statusToDelete.id);
        setDeleteConfirmOpen(false);
        try {
            const result = yield ObjectStatusApi.remove(statusToDelete.id);
            if (result.status === 200) {
                toast.success(translate("ObjectStatusDeletedSuccessfully"));
                // Intelligent pagination handling after deletion
                // For pagination, we only care about Level 1 items being deleted
                const level1ItemsToDelete = statusToDelete.level === 1 ? 1 : 0;
                // Calculate if current page will be empty after deletion
                const currentTotalLevel1Items = level1Statuses.length;
                const remainingLevel1Items = currentTotalLevel1Items - level1ItemsToDelete;
                const totalPages = Math.ceil(remainingLevel1Items / rowsPerPage);
                // If current page will be empty, go to previous page (but not below 0)
                if (remainingLevel1Items > 0 && page >= totalPages) {
                    setPage(Math.max(0, totalPages - 1));
                }
                setRefreshGrid(!refreshGrid);
                setStatusToDelete(null);
            }
        }
        catch (error) {
            const errorMessage = ((_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error) || ((_c = error === null || error === void 0 ? void 0 : error.response) === null || _c === void 0 ? void 0 : _c.data) || (error === null || error === void 0 ? void 0 : error.message);
            // Handle specific error cases
            if (errorMessage === null || errorMessage === void 0 ? void 0 : errorMessage.includes("system status")) {
                toast.error(translate("CannotDeleteSystemStatus"));
            }
            else if (errorMessage === null || errorMessage === void 0 ? void 0 : errorMessage.includes("has children")) {
                toast.error(translate("CannotDeleteStatusWithChildren"));
            }
            else if (errorMessage === null || errorMessage === void 0 ? void 0 : errorMessage.includes("used by objects")) {
                toast.error(translate("CannotDeleteStatusInUse"));
            }
            else {
                toast.error(translate("ErrorDeleteObjectStatus"));
            }
        }
        finally {
            setDeleting(false);
            setIdToBeDeleted(undefined);
        }
    });
    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setStatusToDelete(null);
    };
    // Count children recursively
    const countChildren = (node) => {
        if (!node.children || node.children.length === 0)
            return 0;
        let count = node.children.length;
        node.children.forEach((child) => {
            count += countChildren(child);
        });
        return count;
    };
    const handleNewStatus = () => {
        setStatus(initialFormData);
        setModalTitle("NewObjectStatus");
        setStatusModalOpen(true);
    };
    const handleEditStatus = (statusToEdit) => {
        setStatus(statusToEdit);
        setModalTitle("UpdateObjectStatus");
        setStatusModalOpen(true);
    };
    const onCloseObjectStatusModal = () => {
        setStatusModalOpen(false);
        setStatus(initialFormData);
    };
    const columns = [
        { id: "name", label: translate("Name"), minWidth: 200, align: "left" },
        { id: "level", label: translate("Level"), minWidth: 100, align: "left" },
        {
            id: "isActive",
            label: translate("Status"),
            minWidth: 100,
            align: "left",
        },
    ];
    // Only show Action column if user has Full Access (partial access is read-only for ObjectStatus management)
    if (isFullAccess) {
        columns.push({
            id: "action",
            label: translate("Action"),
            minWidth: 100,
            align: "left",
        });
    }
    // Memoized filtered statuses and nodes to expand
    const filterResult = useMemo(() => {
        return filterTree(statuses, searchQuery);
    }, [statuses, searchQuery]);
    // Auto-expand nodes when search results change
    useEffect(() => {
        if (searchQuery.trim() && filterResult.nodesToExpand.length > 0) {
            setExpandedRows((prev) => {
                const newExpanded = new Set(prev);
                filterResult.nodesToExpand.forEach((id) => newExpanded.add(id));
                return newExpanded;
            });
        }
    }, [searchQuery, filterResult.nodesToExpand]);
    const filteredStatuses = filterResult.filtered;
    // Memoized flattened and visible statuses
    const flatStatuses = useMemo(() => flattenTree(filteredStatuses, expandedRows).filter((status) => status.isVisible), [filteredStatuses, expandedRows]);
    // Get only Level 1 statuses for pagination count
    const level1Statuses = useMemo(() => filteredStatuses, [filteredStatuses]);
    // A remembered page can point past the end of the list (statuses deleted, or a
    // restored search that now matches fewer rows). Clamp instead of showing an
    // empty table — deliberately only clamps downwards, so the deletion handling
    // above keeps working.
    useEffect(() => {
        if (loading)
            return;
        const lastPage = Math.max(0, Math.ceil(level1Statuses.length / rowsPerPage) - 1);
        if (page > lastPage) {
            setFilters((prev) => (Object.assign(Object.assign({}, prev), { page: lastPage })));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [level1Statuses.length, rowsPerPage, page, loading]);
    // Paginated statuses - paginate by Level 1 only, but include their visible children
    const paginatedStatuses = useMemo(() => {
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        // Get the Level 1 statuses for this page
        const level1ForPage = level1Statuses.slice(startIndex, endIndex);
        // Now get all visible items (including children) for these Level 1 statuses
        const result = [];
        for (const level1Status of level1ForPage) {
            // Find this status and all its children in the flat list
            const startCollecting = flatStatuses.findIndex(s => s.id === level1Status.id);
            if (startCollecting === -1)
                continue;
            result.push(flatStatuses[startCollecting]);
            // Collect all children until we hit another Level 1 or end of array
            let i = startCollecting + 1;
            while (i < flatStatuses.length && flatStatuses[i].level > 1) {
                result.push(flatStatuses[i]);
                i++;
            }
        }
        return result;
    }, [flatStatuses, level1Statuses, page, rowsPerPage]);
    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (event) => {
        setFilters((prev) => (Object.assign(Object.assign({}, prev), { rowsPerPage: parseInt(event.target.value, 10), page: 0 })));
    };
    return (_jsxs(Layout, { children: [_jsx(ObjectStatusTopHeader, { openPopup: handleNewStatus, translate: translate, searchQuery: searchQuery, onSearchChange: handleSearchChange }), _jsx(Box, { sx: {
                    padding: { xs: "16px 16px", sm: "16px 32px" },
                    bgcolor: theme.palette.common.white,
                    borderRadius: "24px",
                }, children: _jsxs(Paper, { sx: { width: "100%", overflow: "hidden", boxShadow: "none" }, children: [_jsx(TableContainer, { sx: { maxHeight: "calc(100vh - 270px)", overflowY: "auto" }, children: _jsxs(Table, { stickyHeader: true, "aria-label": "sticky table", children: [_jsx(TableHead, { children: _jsx(TableRow, { sx: { backgroundColor: theme.palette.common.white }, children: columns.map((column) => (_jsx(StyledTableCell, { align: column.align, style: { minWidth: column.minWidth }, children: _jsx(Typography, { variant: "h6", sx: {
                                                        color: theme.palette.text.primary,
                                                        fontSize: {
                                                            xs: "0.875rem",
                                                            sm: "1rem",
                                                            md: "1.25rem",
                                                        },
                                                    }, children: column.label }) }, column.id))) }) }), _jsx(TableBody, { children: loading ? (_jsx(TableRowsLoader, { rowsNum: 10, cellNum: columns.length })) : level1Statuses.length === 0 ? (_jsx(StyledTableRow, { children: _jsx(StyledTableCell, { colSpan: columns.length, align: "center", children: _jsx(Typography, { variant: "body2", sx: {
                                                        color: "text.secondary",
                                                        py: 4
                                                    }, children: searchQuery
                                                        ? translate("NoSearchResults") || "No results found"
                                                        : translate("NoDataAvailable") }) }) })) : paginatedStatuses.length === 0 ? (_jsx(StyledTableRow, { children: _jsx(StyledTableCell, { colSpan: columns.length, align: "center", children: _jsx(Typography, { variant: "body2", sx: {
                                                        color: "text.secondary",
                                                        py: 4
                                                    }, children: translate("NoDataOnThisPage") || "No data on this page" }) }) })) : (paginatedStatuses.map((status) => (_jsxs(StyledTableRow, { sx: {
                                                "&:hover": {
                                                    backgroundColor: theme.palette.action.hover,
                                                },
                                            }, children: [_jsx(StyledTableCell, { align: "left", children: _jsxs(Box, { sx: { display: "flex", alignItems: "center" }, children: [status.hasChildren ? (_jsx(Box, { onClick: () => toggleRowExpansion(status.id), sx: {
                                                                    cursor: "pointer",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    marginRight: "8px",
                                                                    padding: "4px",
                                                                    borderRadius: "4px",
                                                                    "&:hover": {
                                                                        backgroundColor: theme.palette.action.hover,
                                                                    },
                                                                }, children: expandedRows.has(status.id) ? (_jsx(CaretDown, { size: 20, weight: "bold", color: theme.palette.primary.main })) : (_jsx(CaretRight, { size: 20, weight: "bold", color: theme.palette.primary.main })) })) : (status.level > 1 && (_jsx(Box, { sx: {
                                                                    width: "28px",
                                                                    display: "flex",
                                                                    justifyContent: "center",
                                                                }, children: _jsx(CaretRight, { size: 16, style: {
                                                                        marginRight: "8px",
                                                                        color: theme.palette.text.secondary,
                                                                        opacity: 0.5,
                                                                    } }) }))), _jsx(Typography, { variant: "body2", sx: {
                                                                    fontWeight: status.level === 1 ? 600 : 400,
                                                                    color: status.level === 1
                                                                        ? theme.palette.primary.main
                                                                        : "inherit",
                                                                    paddingLeft: status.level > 1 && !status.hasChildren
                                                                        ? `${(status.level - 1) * 20}px`
                                                                        : "0px",
                                                                }, children: status.name })] }) }), _jsx(StyledTableCell, { align: "left", children: _jsx(Box, { sx: {
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            bgcolor: status.level === 1
                                                                ? "#E3F2FD"
                                                                : status.level === 2
                                                                    ? "#F3E5F5"
                                                                    : "#FFF3E0",
                                                            color: status.level === 1
                                                                ? "#1976D2"
                                                                : status.level === 2
                                                                    ? "#7B1FA2"
                                                                    : "#F57C00",
                                                            borderRadius: "12px",
                                                            px: 2,
                                                            py: 0.5,
                                                            fontSize: "0.875rem",
                                                            fontWeight: 600,
                                                        }, children: status.level }) }), _jsx(StyledTableCell, { align: "left", children: _jsx(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: _jsx(Switch, { checked: status.isActive, onChange: (event) => handleStatusToggle(status.id, event.target.checked), disabled: status.isSystemStatus || loading || !isFullAccess, color: "primary" }) }) }), isFullAccess && (_jsx(StyledTableCell, { align: "left", children: _jsxs(Grid, { container: true, sx: {
                                                            alignContent: "left",
                                                            justifyContent: "left",
                                                            gap: { xs: 3, sm: 1 }
                                                        }, children: [canCreateEdit && (_jsx(Grid, { size: 3, children: _jsx(Box, { sx: {
                                                                        display: "flex",
                                                                        justifyContent: "center",
                                                                        alignItems: "center",
                                                                        bgcolor: status.isSystemStatus
                                                                            ? "#E0E0E0"
                                                                            : "#E3F2FD",
                                                                        borderRadius: "50%",
                                                                        width: "36px",
                                                                        height: "36px",
                                                                        cursor: status.isSystemStatus
                                                                            ? "not-allowed"
                                                                            : "pointer",
                                                                        opacity: status.isSystemStatus ? 0.5 : 1,
                                                                        transition: "all 0.2s",
                                                                    }, title: status.isSystemStatus
                                                                        ? translate("CannotEditSystemStatus")
                                                                        : "", children: _jsx(Pencil, { size: 14, style: { cursor: "pointer" }, onClick: () => !status.isSystemStatus &&
                                                                            handleEditStatus(status) }) }) })), canDelete && (_jsx(Grid, { size: 3, children: _jsx(Box, { sx: {
                                                                        display: "flex",
                                                                        justifyContent: "center",
                                                                        alignItems: "center",
                                                                        bgcolor: status.isSystemStatus
                                                                            ? "#E0E0E0"
                                                                            : "#E3F2FD",
                                                                        borderRadius: "50%",
                                                                        width: "36px",
                                                                        height: "36px",
                                                                        cursor: status.isSystemStatus
                                                                            ? "not-allowed"
                                                                            : "pointer",
                                                                        opacity: status.isSystemStatus ? 0.5 : 1,
                                                                        transition: "all 0.2s",
                                                                    }, title: status.isSystemStatus
                                                                        ? translate("CannotDeleteSystemStatus")
                                                                        : "", children: deleting && idToBeDeleted === status.id ? (_jsx(CircularProgress, { size: 24, color: "inherit" })) : (_jsx(Trash, { onClick: () => !status.isSystemStatus &&
                                                                            handleDeleteClick(status) })) }) }))] }) }))] }, status.id)))) })] }) }), _jsx(TablePagination, { rowsPerPageOptions: [5, 10, 25, 50], component: "div", count: level1Statuses.length, rowsPerPage: rowsPerPage, page: page, onPageChange: handleChangePage, onRowsPerPageChange: handleChangeRowsPerPage, labelRowsPerPage: translate("RowsPerPage") || "Rows per page:", labelDisplayedRows: ({ from, to, count }) => `${from}-${to} ${translate("Of") || "of"} ${count !== -1
                                ? count
                                : `${translate("MoreThan") || "more than"} ${to}`}` })] }) }), _jsxs(Dialog, { open: deleteConfirmOpen, onClose: handleDeleteCancel, "aria-labelledby": "delete-dialog-title", "aria-describedby": "delete-dialog-description", children: [_jsxs(DialogTitle, { id: "delete-dialog-title", sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(Warning, { size: 28, weight: "fill", color: "#FF9800" }), translate("ConfirmDelete")] }), _jsx(DialogContent, { children: _jsx(DialogContentText, { id: "delete-dialog-description", component: "div", children: statusToDelete &&
                                statusToDelete.children &&
                                statusToDelete.children.length > 0 ? (_jsxs(_Fragment, { children: [_jsxs(Typography, { variant: "body1", sx: {
                                            color: "warning.main",
                                            mb: 2,
                                            fontWeight: 600
                                        }, children: ["\u26A0\uFE0F ", translate("DeleteStatusWithChildrenWarning")] }), _jsxs(Typography, { variant: "body2", sx: { mb: 1 }, children: [translate("StatusToDelete"), ":", " ", _jsx("strong", { children: statusToDelete.name })] }), _jsxs(Typography, { variant: "body2", color: "error", sx: { mb: 2 }, children: [translate("TotalItemsToDelete"), ":", " ", _jsx("strong", { children: countChildren(statusToDelete) + 1 })] }), _jsxs(Box, { sx: {
                                            bgcolor: "#FFEBEE",
                                            p: 2,
                                            borderRadius: 1,
                                            border: "1px solid #EF5350",
                                            mb: 2,
                                        }, children: [_jsxs(Typography, { variant: "body2", sx: { fontWeight: 600, mb: 1, color: "#C62828" }, children: ["\uD83D\uDDD1\uFE0F ", translate("ItemsWillBeDeleted")] }), _jsx(Typography, { variant: "body2", sx: {
                                                    color: "text.secondary"
                                                }, children: translate("CascadeDeleteExplanation") })] }), _jsx(Box, { sx: {
                                            bgcolor: "#E3F2FD",
                                            p: 2,
                                            borderRadius: 1,
                                            border: "1px solid #64B5F6",
                                        }, children: _jsxs(Typography, { variant: "body2", sx: {
                                                color: "text.secondary"
                                            }, children: ["\u2139\uFE0F ", translate("DeleteWillFailIfInUse")] }) })] })) : (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "body1", sx: { mb: 2 }, children: translate("DeleteStatusConfirmation") }), _jsx(Typography, { variant: "body2", sx: { mb: 2, fontWeight: 600 }, children: statusToDelete === null || statusToDelete === void 0 ? void 0 : statusToDelete.name }), _jsx(Box, { sx: {
                                            bgcolor: "#FFF9C4",
                                            p: 2,
                                            borderRadius: 1,
                                            border: "1px solid #FBC02D",
                                        }, children: _jsxs(Typography, { variant: "body2", sx: {
                                                color: "text.secondary"
                                            }, children: ["\u26A0\uFE0F ", translate("DeleteStatusWarningNote")] }) })] })) }) }), _jsxs(DialogActions, { sx: { padding: "16px 24px" }, children: [_jsx(Button, { onClick: handleDeleteCancel, variant: "outlined", color: "primary", children: translate("Cancel") }), _jsx(Button, { onClick: handleDeleteConfirm, variant: "contained", color: "error", autoFocus: true, children: (statusToDelete === null || statusToDelete === void 0 ? void 0 : statusToDelete.children) && statusToDelete.children.length > 0
                                    ? translate("DeleteWithChildren")
                                    : translate("Delete") })] })] }), _jsx(ObjectStatusFormSubmit, { open: statusModalOpen, onClose: onCloseObjectStatusModal, onSuccess: () => setRefreshGrid(!refreshGrid), initialFormData: initialFormData, data: status, title: translate(modalTitle), allStatuses: statuses })] }));
};
export default ObjectStatus;
